import i18next from 'i18next';
import SspProviderContext from '../../utils/SspProvider/context';
import { daysBetweenNowAnd } from '../../utils/daysBetweenNowAnd';
import { FloatingCancelXButton } from '../AutostadtTabComponents/Button';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import type { AnimationEvent, CSSProperties } from 'react';

const COUNTDOWN_START = 30;

interface DaysCountdownProps extends WordsCountdownProps {
    scrolling: boolean;
    target: number;
}

interface IntroProps {
    onEnded: () => void;
    onIntroCanceled: () => void;
    videos: [mood: Video, camflight: Video];
}

interface Video {
    src: string;
    type?: string;
}

interface VideoProps {
    autoplay: boolean;
    id: string;
    loop: boolean;
    onEnded?: () => void;
    play?: boolean;
    preload?: 'auto' | 'metadata' | 'none';
    src: string;
    type?: string;
}

interface WordsCountdownProps {
    animate: boolean;
    onAnimationEnd: () => void;
}

const getDaysLeft = (appointment?: string): number => {
    if (!appointment) {
        return COUNTDOWN_START;
    }
    const days = daysBetweenNowAnd(appointment);

    return days > COUNTDOWN_START ? COUNTDOWN_START : days;
};

function Video(props: VideoProps): JSX.Element {
    const videoElementRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (props.play) {
            videoElementRef.current?.play();
        } else {
            videoElementRef.current?.pause();
        }
    }, [props.play]);

    const handleOnEnded = () => {
        props.onEnded?.();
    };

    return (
        <video
            id={props.id}
            autoPlay={props.autoplay}
            controlsList='nodownload'
            disablePictureInPicture
            loop={props.loop}
            muted
            playsInline
            preload={props.preload ?? 'none'}
            onEnded={handleOnEnded}
            ref={videoElementRef}
        >
            <source src={props.src} type={props.type} />
        </video>
    );
}

function DaysCountdown(props: DaysCountdownProps): JSX.Element {
    const daysFirst = new Set();
    const daysSecond: number[] = [];

    if (props.scrolling) {
        const limit = ((COUNTDOWN_START - props.target) % 10) + 1;

        for (let i = COUNTDOWN_START; i >= 0; i--) {
            daysFirst.add(i < 10 ? 0 : Math.trunc(i / 10));

            if (daysSecond.length < limit) {
                daysSecond.push(i % 10);
            }

            if (props.target >= COUNTDOWN_START || i <= props.target) {
                break;
            }
        }
    } else {
        if (props.target >= 10) {
            daysFirst.add(Number(String(props.target).split('')[0]));
        }

        daysSecond.push(props.target % 10);
    }

    const handleAnimationStart = (e: AnimationEvent) => {
        if ((e.target as HTMLElement).classList.contains('countdown-days') && e.animationName === 'intro-slide-out') {
            props.onAnimationEnd();
        }
    };

    return (
        <div className={`countdown-days${props.animate ? ' slide-out' : ''}`} onAnimationStart={handleAnimationStart}>
            <span className='word'>Noch</span>
            <span className={`days${!props.scrolling && props.target < 10 ? ' single' : ''}`}>
                <span className='first' style={{ '--count': daysFirst.size } as CSSProperties}>
                    <span className={`track${props.animate ? ' scroll' : ''}`}>
                        {Array.from(daysFirst).map((item, index) => (
                            <span className='day' key={index} style={{ '--index': index } as CSSProperties}>
                                {String(item)}
                            </span>
                        ))}
                    </span>
                </span>
                <span className='second' style={{ '--count': daysSecond.length } as CSSProperties}>
                    <span className={`track${props.animate ? ' scroll' : ''}`}>
                        {daysSecond.map((item, index) => (
                            <span className='day' key={index} style={{ '--index': index } as CSSProperties}>
                                {String(item)}
                            </span>
                        ))}
                    </span>
                </span>
            </span>
            <span className='word'>{props.target === 1 ? 'Tag' : 'Tage'}</span>
        </div>
    );
}

function WordsCountdown(props: WordsCountdownProps): JSX.Element {
    const handleAnimationStart = (e: AnimationEvent) => {
        if (e.animationName === 'intro-slide-back') {
            props.onAnimationEnd();
        }
    };

    const words = [i18next.t('Anticipation'), i18next.t('Excitement'), i18next.t('Curiosity')];

    return (
        <div
            className={`countdown-words${props.animate ? ' slide-in-out' : ''}`}
            style={{ '--count': words.length } as CSSProperties}
            onAnimationStart={handleAnimationStart}
        >
            {words.map((word, index) => (
                <span
                    className={`word${props.animate ? ' slide-in-out' : ''}`}
                    key={index}
                    style={{ '--index': index } as CSSProperties}
                >
                    {word}
                </span>
            ))}
        </div>
    );
}

export default function Intro(props: IntroProps): JSX.Element {
    const store = useContext(SspProviderContext);
    const daysLeft = getDaysLeft(
        store.state.content?.asModel.arr('market_specific_list').find(obj => obj.Key === 'APPOINTMENT_DATE')?.Value
    );

    const bottomElementRef = useRef<HTMLDivElement>(null);
    const buttonContainerElementRef = useRef<HTMLDivElement>(null);
    const containerElementRef = useRef<HTMLDivElement>(null);
    const coverElementRef = useRef<HTMLDivElement>(null);
    const headerElementRef = useRef<HTMLDivElement>(null);
    const topElementRef = useRef<HTMLDivElement>(null);

    const [camflightCanceled, setCamflightCanceled] = useState<boolean>(false);
    const [countDownDays, setCountDownDays] = useState<boolean>(false);
    const [countDownWords, setCountDownWords] = useState<boolean>(false);
    const [moodCanceled, setMoodCanceled] = useState<boolean>(true);

    const handleAnimationEnd = useCallback(
        (e: AnimationEvent) => {
            if ((e.target as HTMLElement).classList.contains('cover') && e.animationName === 'intro-fade-out') {
                bottomElementRef.current?.classList.add('shrink');
                coverElementRef.current?.classList.add('reset-z-index');
                topElementRef.current?.classList.add('shrink');
            }

            if ((e.target as HTMLElement).classList.contains('cover') && e.animationName === 'intro-fade-in') {
                containerElementRef.current?.classList.add('fade-out');
            }

            if (
                (e.target as HTMLElement).classList.contains('intro-container') &&
                e.animationName === 'intro-fade-out'
            ) {
                props.onEnded();
            }

            if ((e.target as HTMLElement).classList.contains('top') && e.animationName === 'intro-maximize-top') {
                setCamflightCanceled(true);
            }

            if (e.animationName === 'intro-shrink-bottom') {
                bottomElementRef.current?.classList.add('reset-z-index');
                bottomElementRef.current?.classList.remove('maximize');
                if (!countDownDays) {
                    setCountDownDays(true);
                }
                if (moodCanceled) {
                    setMoodCanceled(false);
                }
            }
        },
        [countDownDays, moodCanceled, props]
    );

    const handleDaysCountdownEnd = useCallback(() => {
        if (!countDownWords) {
            setCountDownWords(true);
        }
    }, [countDownWords]);

    const handleWordsCountdownEnd = () => {
        headerElementRef.current?.classList.add('reveal');
        topElementRef.current?.classList.add('maximize');
    };

    const handleIntroEnded = () => {
        buttonContainerElementRef.current?.classList.add('fade-out');
        coverElementRef.current?.classList.add('fade-in');
        localStorage.setItem('introShown', String(true));
        setCamflightCanceled(true);
        setMoodCanceled(true);
    };

    const handleCancelIntro = () => {
        setCamflightCanceled(true);
        setMoodCanceled(true);
        props.onIntroCanceled();
    };

    return (
        <div className='intro-container' ref={containerElementRef} onAnimationEnd={handleAnimationEnd}>
            <div className='intro'>
                <div className='cover' ref={coverElementRef} onAnimationEnd={handleAnimationEnd}></div>
                <div className='header' ref={headerElementRef}>
                    <svg className='header-icon'>
                        <use xlinkHref='#autostadt' />
                    </svg>
                </div>
                <div className='top' ref={topElementRef} onAnimationEnd={handleAnimationEnd}>
                    <div className='inner'>
                        <Video
                            autoplay={false}
                            id='intro-mood'
                            loop={false}
                            onEnded={handleIntroEnded}
                            play={!moodCanceled}
                            preload={'auto'}
                            src={props.videos[0].src}
                            type={props.videos[0].type}
                        />
                    </div>
                </div>
                <div className='center'>
                    <div className='inner'>
                        <WordsCountdown animate={countDownWords} onAnimationEnd={handleWordsCountdownEnd} />
                        <DaysCountdown
                            animate={countDownDays}
                            onAnimationEnd={handleDaysCountdownEnd}
                            scrolling={true}
                            target={daysLeft}
                        />
                    </div>
                </div>
                <div className='bottom maximize' ref={bottomElementRef} onAnimationEnd={handleAnimationEnd}>
                    <div className='inner'>
                        {!camflightCanceled && (
                            <Video
                                autoplay={true}
                                id='intro-camflight'
                                loop={false}
                                play={true}
                                src={props.videos[1].src}
                                type={props.videos[1].type}
                            />
                        )}
                    </div>
                </div>
                <div className='button-container' ref={buttonContainerElementRef}>
                    <FloatingCancelXButton onClick={handleCancelIntro} />
                </div>
            </div>
        </div>
    );
}
